/* corex-munequera-draw.js · Dibujo del personalizador de muñequeras (canvas).
   - Editor: la cara a tamaño real en mm (corte 90 × 150 con su sangrado), guías de corte y zona segura y la selección.
   - Vista previa: la foto real del modelo con el diseño encima (multiply). El diseño se escala igual en los dos ejes
     (alto del corte = alto del tejido calibrado en la foto) y el sangrado rellena los lados: no se deforma.
   - Archivo de impresión: la cara con sangrado (96 × 156 mm) a PRINT_DPI, sin guías.
   - Patrón: los motivos (corex-munequera-pattern.js) cubren toda la cara con sangrado. En pantalla se pintan con
     miniaturas (getMotif); el archivo de impresión, siempre con las imágenes originales (getImage).
   Depende de CorexMunequeraModel, CorexMunequeraPattern y CorexTextilFonts. */
(function (global) {
  'use strict';

  var M = global.CorexMunequeraModel;
  var F = global.CorexTextilFonts;
  var P = global.CorexMunequeraPattern;
  var PRINT_DPI = 300;
  var INCH_MM = 25.4;
  var SEL = '#1D6FE0';
  var GUIDE_CUT = '#6E6667';
  var GUIDE_SAFE = '#D31F26';
  var WARN = '#E0A100';
  var HANDLE_PX = 8;
  var ROT_HANDLE_PX = 18;
  var BLEED_VEIL = 'rgba(244, 242, 238, 0.72)';
  /* Radio de las esquinas del tejido en la foto (fracción del ancho de la foto), como la versión anterior. */
  var PHOTO_RADIUS = 0.021;

  function fontOf(l) {
    var f = F && F.get(l.fontKey);
    return f ? { family: f.family, weight: f.weight } : { family: 'sans-serif', weight: 700 };
  }
  function textFont(l, factor) { var f = fontOf(l); return f.weight + ' ' + (l.fontSize * factor) + 'px ' + f.family; }
  var measureCtx = null;
  /* Ancho del texto en mm a su fontSize (se mide a ×10 para no perder precisión con fuentes pequeñas). */
  function measureText(l) {
    measureCtx = measureCtx || document.createElement('canvas').getContext('2d');
    measureCtx.font = textFont(l, 10);
    return measureCtx.measureText(l.text || ' ').width / 10;
  }
  function layerSize(l) { return M.layerSize(l, measureText); }

  function drawLayers(ctx, layers, getImage) {
    layers.forEach(function (l) {
      ctx.save();
      ctx.translate(l.x, l.y);
      ctx.rotate(M.rad(l.rotation));
      if (l.type === 'image') {
        var img = getImage(l.src);
        if (img && img.complete && img.naturalWidth) {
          var s = layerSize(l);
          ctx.drawImage(img, -s.w / 2, -s.h / 2, s.w, s.h);
        }
      } else {
        ctx.scale(0.1 * l.scale, 0.1 * l.scale);
        ctx.font = textFont(l, 10);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = l.color;
        ctx.fillText(l.text, 0, 0);
      }
      ctx.restore();
    });
  }
  /* Fondo + capas (o patrón) sobre todo el área de impresión (con sangrado), en mm. */
  function drawFaceContent(ctx, face, getImage, getMotif) {
    var p = M.GEO.print, s = M.rectSize(p);
    ctx.save();
    ctx.beginPath();
    ctx.rect(p[0], p[1], s.w, s.h);
    ctx.clip();
    ctx.fillStyle = face.background;
    ctx.fillRect(p[0], p[1], s.w, s.h);
    if (face.pattern) P.draw(ctx, face.pattern, p, getMotif || getImage);
    else drawLayers(ctx, face.layers, getImage);
    ctx.restore();
  }

  function drawSelection(ctx, l, pxMm, warn) {
    var s = layerSize(l), hs = HANDLE_PX * pxMm, rot = ROT_HANDLE_PX * pxMm;
    ctx.save();
    ctx.translate(l.x, l.y);
    ctx.rotate(M.rad(l.rotation));
    ctx.strokeStyle = warn ? WARN : SEL;
    ctx.lineWidth = 1.4 * pxMm;
    ctx.setLineDash([]);
    ctx.strokeRect(-s.w / 2, -s.h / 2, s.w, s.h);
    ctx.beginPath();
    ctx.moveTo(0, -s.h / 2);
    ctx.lineTo(0, -s.h / 2 - rot);
    ctx.stroke();
    ctx.fillStyle = '#FFFFFF';
    [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(function (c) {
      ctx.fillRect(c[0] * s.w / 2 - hs / 2, c[1] * s.h / 2 - hs / 2, hs, hs);
      ctx.strokeRect(c[0] * s.w / 2 - hs / 2, c[1] * s.h / 2 - hs / 2, hs, hs);
    });
    ctx.beginPath();
    ctx.arc(0, -s.h / 2 - rot, hs * 0.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  /* Tamaño en CSS y en píxeles de un canvas que muestra el área de impresión con `pad` mm alrededor. */
  function sizeEditor(canvas, cssW, pad) {
    var dpr = Math.min(global.devicePixelRatio || 1, 3);
    var p = M.rectSize(M.GEO.print);
    var cssPpm = cssW / (p.w + pad * 2);
    var cssH = Math.round((p.h + pad * 2) * cssPpm);
    canvas.style.width = cssW + 'px';
    canvas.style.height = cssH + 'px';
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    return { cssPpm: cssPpm, pad: pad, dpr: dpr };
  }
  /* mm de la cara → píxeles CSS del canvas del editor (para los controles HTML encima). */
  function editorOrigin(metrics) { return metrics.pad - M.GEO.print[0]; }

  /* opts: { face, selected, warn, getImage, getMotif, cssPpm, pad, dpr } */
  function drawEditor(canvas, opts) {
    var ctx = canvas.getContext('2d');
    var scale = opts.cssPpm * opts.dpr, pxMm = 1 / opts.cssPpm, o = editorOrigin(opts);
    var g = M.GEO, p = g.print, ps = M.rectSize(p);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(scale, 0, 0, scale, o * scale, o * scale);

    /* Fuera del área de impresión, las capas se ven tenues (para poder recolocar lo que se sale). */
    if (!opts.face.pattern) {
      ctx.save();
      ctx.globalAlpha = 0.22;
      drawLayers(ctx, opts.face.layers, opts.getImage);
      ctx.restore();
    }
    drawFaceContent(ctx, opts.face, opts.getImage, opts.getMotif);

    /* Velo sobre el sangrado: se imprime, pero se corta. */
    ctx.save();
    ctx.beginPath();
    ctx.rect(p[0], p[1], ps.w, ps.h);
    ctx.rect(g.cut[2], g.cut[1], -g.w, g.h);
    ctx.fillStyle = BLEED_VEIL;
    ctx.fill('evenodd');
    ctx.restore();

    ctx.lineWidth = 1.3 * pxMm;
    ctx.setLineDash([]);
    ctx.strokeStyle = GUIDE_CUT;
    ctx.strokeRect(g.cut[0], g.cut[1], g.w, g.h);
    ctx.setLineDash([5 * pxMm, 4 * pxMm]);
    ctx.strokeStyle = GUIDE_SAFE;
    var ss = M.rectSize(g.safe);
    ctx.strokeRect(g.safe[0], g.safe[1], ss.w, ss.h);
    ctx.setLineDash([]);
    if (opts.selected) drawSelection(ctx, opts.selected, pxMm, opts.warn);
  }

  /* Contenido de una cara (con sangrado) en un canvas propio a `ppm` píxeles por mm. */
  function contentCanvas(face, getImage, ppm, getMotif) {
    var p = M.GEO.print, ps = M.rectSize(p);
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(ps.w * ppm));
    c.height = Math.max(1, Math.round(ps.h * ppm));
    var ctx = c.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.setTransform(ppm, 0, 0, ppm, -p[0] * ppm, -p[1] * ppm);
    drawFaceContent(ctx, face, getImage, getMotif);
    return c;
  }

  /* Foto del modelo con el diseño. area: fracciones { x, y, w, h } del tejido en la foto. Sin foto: rectángulo plano.
     opts.cutout: solo la muñequera, con el fondo transparente (imagen para redes). */
  function drawPhotoFace(canvas, face, photo, area, getImage, getMotif, opts) {
    var MK = global.CorexMunequeraMockup;
    if (opts && opts.cutout) {
      drawPhotoFace(canvas, face, photo, area, getImage, getMotif);
      var c2 = canvas.getContext('2d');
      if (photo && MK && MK.cutout(c2, canvas.width, canvas.height, photo)) return;
      c2.save();
      c2.globalCompositeOperation = 'destination-in';
      c2.fillRect(area.x * canvas.width, area.y * canvas.height, area.w * canvas.width, area.h * canvas.height);
      c2.restore();
      return;
    }
    var ctx = canvas.getContext('2d');
    var W = canvas.width, H = canvas.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, W, H);
    if (photo) ctx.drawImage(photo, 0, 0, W, H);
    var a = photo ? area : { x: 0.04, y: 0.04, w: 0.92, h: 0.92 };
    var ax = a.x * W, ay = a.y * H, aw = a.w * W, ah = a.h * H;
    var ppm = ah / M.GEO.h;
    var content = contentCanvas(face, getImage, ppm, getMotif);
    var p = M.GEO.print;
    var dx = ax + aw / 2 - (M.GEO.w / 2 - p[0]) * ppm;
    var dy = ay + ah / 2 - (M.GEO.h / 2 - p[1]) * ppm;
    /* Con foto: silueta, sombreado y curvatura reales (corex-munequera-mockup.js); si no se puede, en plano. */
    if (photo && MK && MK.draw(ctx, W, H, photo, { x: ax, y: ay, w: aw, h: ah }, content, dx, dy, face.background)) return;
    ctx.save();
    ctx.beginPath();
    var r = PHOTO_RADIUS * W;
    if (ctx.roundRect) ctx.roundRect(ax, ay, aw, ah, r); else ctx.rect(ax, ay, aw, ah);
    ctx.clip();
    ctx.globalCompositeOperation = photo ? 'multiply' : 'source-over';
    ctx.drawImage(content, dx, dy);
    ctx.restore();
    ctx.globalCompositeOperation = 'source-over';
    if (!photo) {
      ctx.strokeStyle = '#CFC9C0';
      ctx.lineWidth = 1;
      ctx.strokeRect(ax + 0.5, ay + 0.5, aw - 1, ah - 1);
    }
  }

  /* Archivo de impresión de una cara: 96 × 156 mm a PRINT_DPI (1134 × 1843 px). */
  function renderPrint(face, getImage) {
    return contentCanvas(face, getImage, PRINT_DPI / INCH_MM);
  }

  global.CorexMunequeraDraw = {
    PRINT_DPI: PRINT_DPI, HANDLE_PX: HANDLE_PX, ROT_HANDLE_PX: ROT_HANDLE_PX,
    fontOf: fontOf, measureText: measureText, layerSize: layerSize, sizeEditor: sizeEditor, editorOrigin: editorOrigin,
    drawEditor: drawEditor, drawPhotoFace: drawPhotoFace, renderPrint: renderPrint, contentCanvas: contentCanvas
  };
})(this);
